import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import styles from './Carousel.module.css';

interface Banner {
  id?: string | number;
  title: string;
  buttonText?: string;
  background?: string;
  description?: string;
}

interface CarouselProps {
  banners: Banner[];
  onBannerClick?: (banner: Banner) => void;
}

const Carousel: React.FC<CarouselProps> = ({ banners, onBannerClick }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);
  const scrollDebounceRef = useRef<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  console.log(banners, 'banners');
  useEffect(() => {
    if (!scrollRef.current) return;
    if (!Array.isArray(banners) || banners.length < 2) return;

    const wrapper = scrollRef.current;
    let destroyed = false;

    const scrollToIndex = (index: number) => {
      const items = wrapper.querySelectorAll(`.${styles.bannerItem}`);
      const target = items[index] as HTMLElement | undefined;
      if (!target) return;
      const targetRect = target.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      const delta = targetRect.left - wrapperRect.left;
      const left = wrapper.scrollLeft + delta;
      wrapper.scrollTo({ left, behavior: 'smooth' });
    };

    const clearTimer = () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const scheduleNext = (delayMs = 5000) => {
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        setCurrentIndex((prev) => {
          const next = prev + 1;
          const targetIndex = next >= banners.length ? 0 : next;
          scrollToIndex(targetIndex);
          return targetIndex;
        });
      }, delayMs);
    };

    const onUserInteractStart = () => {
      clearTimer();
    };

    const updateIndexToNearest = () => {
      const items = Array.from(
        wrapper.querySelectorAll(`.${styles.bannerItem}`),
      ) as HTMLElement[];
      if (!items.length) return;
      const wrapperRect = wrapper.getBoundingClientRect();
      let nearest = 0;
      let best = Number.POSITIVE_INFINITY;
      items.forEach((el, idx) => {
        const dx = Math.abs(el.getBoundingClientRect().left - wrapperRect.left);
        if (dx < best) {
          best = dx;
          nearest = idx;
        }
      });
      setCurrentIndex(nearest);
    };

    const onUserInteractEndDebounced = () => {
      if (scrollDebounceRef.current !== null) {
        window.clearTimeout(scrollDebounceRef.current);
      }
      scrollDebounceRef.current = window.setTimeout(() => {
        updateIndexToNearest();
        scheduleNext(5000);
      }, 200);
    };

    wrapper.addEventListener('wheel', onUserInteractStart, { passive: true });
    wrapper.addEventListener('pointerdown', onUserInteractStart, {
      passive: true,
    });
    wrapper.addEventListener('touchstart', onUserInteractStart, {
      passive: true,
    });
    wrapper.addEventListener('scroll', onUserInteractEndDebounced, {
      passive: true,
    });

    scheduleNext(5000);

    const handleResize = () => {
      if (destroyed) return;
      scrollToIndex(currentIndex);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      destroyed = true;
      clearTimer();
      if (scrollDebounceRef.current !== null) {
        window.clearTimeout(scrollDebounceRef.current);
        scrollDebounceRef.current = null;
      }
      wrapper.removeEventListener(
        'wheel',
        onUserInteractStart as EventListener,
      );
      wrapper.removeEventListener(
        'pointerdown',
        onUserInteractStart as EventListener,
      );
      wrapper.removeEventListener(
        'touchstart',
        onUserInteractStart as EventListener,
      );
      wrapper.removeEventListener(
        'scroll',
        onUserInteractEndDebounced as EventListener,
      );
      window.removeEventListener('resize', handleResize);
    };
  }, [banners, currentIndex]);

  return (
    <div ref={scrollRef} className={styles.bannersWrapper}>
      <div className={styles.bannersContainer}>
        {banners.map((banner, index) => (
          <motion.div
            key={banner.id || index}
            className={styles.bannerItem}
            onClick={() => onBannerClick && onBannerClick(banner)}
          >
            <div
              className={styles.bannerItemBackground}
              style={
                banner.background
                  ? ({
                      ['--banner-bg' as unknown as string]: `url(${banner.background})`,
                    } as React.CSSProperties)
                  : undefined
              }
            ></div>
            <div className={styles.bannerItemOverlay}></div>
            <div className={styles.bannerItemContent}>
              <div className={styles.bannerItemTitle}>{banner.title}</div>
              <div className={styles.bannerItemDescription}>
                {banner.description}
              </div>
              <div className={styles.bannerItemAction}>
                <div className={styles.bannerItemActionText}>Подробнее</div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default Carousel;
